/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import SimpleTooltip from '../../../simple-tooltip';
import { DATE, formatDate } from '../../../../utils/date';
import { Button, Modal, ModalBody } from 'reactstrap';
import G17Client from '../../../../services/G17Client';
import { UTRVValidatorResponse } from '../../../../types/utrvProof';
import Loader from '../../../loader';

const LEVEL = {
  WARNING: 'warning',
  ERROR: 'error'
};

const UTRVProofModal = ({ utrv, hide }: { utrv?: { _id: string, ledgerUniversalTrackerValue?: any }, hide: () => void }) => {

  const [proof, setProof] = React.useState<UTRVValidatorResponse>();

  React.useEffect(() => {
    if (!utrv || proof !== undefined) {
      return;
    }
    G17Client.getProofByUtrvId(utrv._id)
      .then(r => setProof(r));
  }, [utrv, proof]);

  if (!utrv || !utrv.ledgerUniversalTrackerValue || !proof) {
    return <Loader />;
  }

  const dataChecks = [
    {
      label: 'Ledger Status',
      level: LEVEL.ERROR,
      value: proof.qldb.valid ? 'OK' : 'Unable to get ledger status',
      check: proof.qldb.valid
    },
    {
      label: 'Ethereum Last Updated',
      level: LEVEL.ERROR,
      value: proof.ethereum.transaction?.timeStamp ? formatDate(new Date(Number(proof.ethereum.transaction?.timeStamp) * 1000), DATE.DEFAULT_SPACES_WITH_TIME) : '',
      note: 'Note: Ethereum will be updated once a day only',
      check: proof.ethereum.valid
    },
    {
      label: 'Proof',
      level: LEVEL.WARNING,
      value: proof.proof.valid ? 'The ledger digests matched' : 'The ledger digests could not be verified',
      note: 'Note: Proof is coming soon',
      check: proof.proof.valid
    },
    {
      label: 'Value Status',
      level: LEVEL.ERROR,
      value: proof.utrv.qldbDocumentId ? 'The value is correctly stored on the ledger' : 'The value is not stored in the ledger',
      check: Boolean(proof.utrv.qldbDocumentId)
    },
    {
      label: 'Value Validation',
      level: LEVEL.ERROR,
      value: proof.utrv.latest.valid ? 'The latest values match the ledger' : 'The latest values do not match the ledger',
      note: 'Note: It can take up to 10 mins after a value is updated before the ledger can validate.',
      check: proof.utrv.latest.valid
    },
    {
      label: 'Value History Validation',
      level: LEVEL.WARNING,
      value: proof.utrv.history.valid ? 'The value history is consistent' : 'The value history is not consistent',
      check: proof.utrv.history.valid
    },
  ];

  const overallCheck = dataChecks.filter(d => d.level === LEVEL.ERROR).every(d => d.check === true);

  const getIcon = (d: { level: string, check: boolean }) => {
    if (d.check) {
      return 'fa-check-circle text-ThemeSuccessMedium';
    }
    if (d.level === LEVEL.WARNING) {
      return 'fa-question-circle text-ThemeAccentMedium';
    }
    return 'fa-times-circle text-ThemeDangerMedium';
  }

  return <Modal isOpen={true} toggle={hide} backdrop='static'>
    <ModalBody>
      <div className='d-flex p-3 align-items-center border-bottom border-colour8'>
        <div>
          <i className={'fa fa-cubes text-ThemeAccentExtradark fa-2x mr-3'} />
        </div>
        <div className='h2 m-0 d-inline-block mr-auto'>
          Ledger Consistency Check
          </div>
        <div>
          {overallCheck
            ?
            <SimpleTooltip text='All data is correctly matched against an independent public ledger that guarantees it has not been manipulated.'>
              <span className='h2 badge background-ThemeSuccessMedium text-ThemeTextWhite'>OK</span>
            </SimpleTooltip>
            :
            <SimpleTooltip text='There is a difference between G17Eco and the public Ledger. Please notify Customer Support as soon as possible.'>
              <span className='h2 badge background-ThemeDangerMedium text-ThemeTextWhite'>Mismatch</span>
            </SimpleTooltip>
          }
        </div>
      </div>
      <div className='d-flex mt-3 flex-column'>
        {dataChecks.map((d, i) => (
          <div key={`${d.label}-${i}`} className='d-flex align-items-center p-3 border-bottom border-ThemeBgDisabled'>
            <div>
              <i className={`fa ${getIcon(d)} fa-2x mr-3`} />
            </div>
            <div>
              <div><strong>{d.label}</strong></div>
              <div>{d.value}</div>
              <div className='text-ThemeTextPlaceholder'>{d.note}</div>
            </div>
          </div>
        ))}
      </div>
      <div className='mt-3 text-right'>
        <Button onClick={hide}>Close</Button>
      </div>
    </ModalBody>
  </Modal>
}

export default UTRVProofModal;
